import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CloudUpload, Loader2, CheckCircle2, AlertCircle, XCircle, HourglassIcon, RotateCcw } from "lucide-react";

const STATUS_COLORS = {
  ready:      { pill: "bg-emerald-500/20 border-emerald-500/40 text-emerald-400", Icon: CheckCircle2 },
  queued:     { pill: "bg-cyan-500/20 border-cyan-500/40 text-cyan-400", Icon: HourglassIcon },
  publishing: { pill: "bg-fuchsia-500/20 border-fuchsia-500/40 text-fuchsia-400", Icon: Loader2 },
  failed:     { pill: "bg-rose-500/20 border-rose-500/40 text-rose-400", Icon: XCircle },
  none:       { pill: "bg-zinc-500/10 border-zinc-500/30 text-zinc-400", Icon: AlertCircle },
};

export default function PublishTodayPanel({ channel, canWrite = true }) {
  const [status, setStatus] = useState(null); // {total, ready, queued, publishing, failed, none, assets:[]}
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/cloudflare-stream/channel/${channel.id}/publish-today-status`);
      setStatus(r.data);
    } catch { /* silent — operator may lack access on some sub-routes */ }
  };

  const retry = async (asset) => {
    if (!canWrite) return;
    setRetryingId(asset.id);
    try {
      await api.post(`/cloudflare-stream/publish/${asset.id}`, null, { timeout: 90000 });
      toast.success(`Re-published "${asset.name}"`);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // The public ingress can rewrite 5xx bodies; fall back to a generic
      // message rather than showing raw HTML text in a toast.
      const msg = typeof detail === "string" && detail.length < 200
        ? detail
        : `Retry failed for "${asset.name}"`;
      toast.error(msg);
    }
    setRetryingId(null);
  };

  useEffect(() => { load(); /* first mount */ }, [channel.id]);

  // Poll every 4s while anything is queued/publishing OR while the dialog is open.
  useEffect(() => {
    if (!status) return;
    const active = status.queued > 0 || status.publishing > 0 || open;
    if (!active) return;
    pollRef.current = setInterval(load, 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [status?.queued, status?.publishing, open, channel.id]);

  const publish = async () => {
    if (!canWrite) return;
    setBusy(true);
    try {
      const r = await api.post(`/cloudflare-stream/channel/${channel.id}/publish-today`);
      const q = r.data.queued || 0;
      if (q === 0 && (r.data.skipped_ready || 0) > 0) {
        toast.success(`Already on CDN · ${r.data.skipped_ready} assets ready`);
      } else if (q === 0 && (r.data.total || 0) === 0) {
        toast.info("Nothing scheduled today for this channel");
      } else if (q === 0) {
        toast.info(`Nothing to queue · ${r.data.skipped_no_source || 0} without a source file`);
      } else {
        toast.success(`Queued ${q} asset${q === 1 ? "" : "s"} for CDN publish`);
      }
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Publish failed";
      toast.error(msg);
    }
    setBusy(false);
  };

  if (!status || status.total === 0) {
    return null; // nothing scheduled — don't clutter the tile
  }

  const readyPct = Math.round((status.ready / status.total) * 100);
  const anyInFlight = status.queued + status.publishing > 0;
  const anyFailed = status.failed > 0;
  const anyMissing = status.total > status.ready;
  const publishableCount = (status.assets || []).filter(
    (a) => a.cdn_status !== "ready" && a.has_source,
  ).length;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`publish-today-btn-${channel.slug}`}
        title={`Today's schedule · ${status.ready}/${status.total} on CDN`}
        className={`flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border transition-colors ${
          anyFailed
            ? "bg-rose-500/10 border-rose-500/40 text-rose-400 hover:bg-rose-500/20"
            : anyInFlight
              ? "bg-fuchsia-500/10 border-fuchsia-500/40 text-fuchsia-400 hover:bg-fuchsia-500/20"
              : anyMissing
                ? "bg-amber-500/10 border-amber-500/40 text-amber-400 hover:bg-amber-500/20"
                : "bg-emerald-500/10 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/20"
        }`}
      >
        {anyInFlight ? <Loader2 className="w-3 h-3 animate-spin" /> : <CloudUpload className="w-3 h-3" />}
        CDN {status.ready}/{status.total}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-xl" data-testid={`publish-today-dialog-${channel.slug}`}>
          <DialogHeader className="relative pb-4 mb-1">
            <div className="absolute inset-x-0 -top-6 -mx-6 h-1" style={{ background: "var(--brand-gradient)" }} />
            <DialogTitle className="font-display flex items-center gap-2">
              <CloudUpload className="w-5 h-5 text-fuchsia-400" strokeWidth={1.75} />
              Publish today · {channel.name}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground font-mono">
              Every asset scheduled to air on this channel today must be on Cloudflare Stream for the tile to play from CDN. Assets without a source file cannot be pushed.
            </DialogDescription>
          </DialogHeader>

          {/* Summary bar */}
          <div className="rounded-sm border border-border bg-muted/30 px-3 py-2">
            <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-widest">
              <span>{status.ready} / {status.total} ready</span>
              <span className="text-muted-foreground">{readyPct}%</span>
            </div>
            <div className="h-1 mt-1 bg-muted/50 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${readyPct}%` }} />
            </div>
            <div className="mt-2 flex flex-wrap gap-2 text-[10px] font-mono">
              {status.queued > 0 && <span className="px-1.5 py-0.5 rounded-sm bg-cyan-500/20 text-cyan-400">queued: {status.queued}</span>}
              {status.publishing > 0 && <span className="px-1.5 py-0.5 rounded-sm bg-fuchsia-500/20 text-fuchsia-400">publishing: {status.publishing}</span>}
              {status.failed > 0 && <span className="px-1.5 py-0.5 rounded-sm bg-rose-500/20 text-rose-400">failed: {status.failed}</span>}
              {status.none > 0 && <span className="px-1.5 py-0.5 rounded-sm bg-zinc-500/10 text-zinc-400">unpublished: {status.none}</span>}
            </div>
          </div>

          {/* Per-asset list */}
          <div className="max-h-72 overflow-y-auto scrollbar-thin border border-border rounded-sm">
            {status.assets.map((a) => {
              const tone = STATUS_COLORS[a.cdn_status] || STATUS_COLORS.none;
              const Icon = tone.Icon;
              const canRetry = canWrite && a.has_source && (a.cdn_status === "failed" || a.cdn_status === "none");
              return (
                <div
                  key={a.id}
                  className="flex items-center justify-between px-3 py-1.5 border-b border-border/50 last:border-b-0"
                  data-testid={`publish-today-row-${a.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-display truncate">{a.name}</div>
                    {a.cdn_error && (
                      <div className="text-[10px] font-mono text-rose-400 truncate" title={a.cdn_error}>{a.cdn_error}</div>
                    )}
                    {!a.has_source && (
                      <div className="text-[10px] font-mono text-amber-400">No source file uploaded</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 ml-3">
                    <span
                      className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${tone.pill}`}
                      data-testid={`publish-today-status-${a.id}`}
                    >
                      <Icon className={`w-3 h-3 ${a.cdn_status === "publishing" ? "animate-spin" : ""}`} />
                      {a.cdn_status}
                    </span>
                    {canRetry && (
                      <button
                        type="button"
                        onClick={() => retry(a)}
                        disabled={retryingId === a.id}
                        className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-fuchsia-500/40 text-fuchsia-400 hover:bg-fuchsia-500/20 disabled:opacity-50"
                        data-testid={`publish-today-retry-${a.id}`}
                        title={`Re-queue "${a.name}"`}
                      >
                        {retryingId === a.id
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <RotateCcw className="w-3 h-3" />}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {status.assets.some((a) => !a.has_source) && (
            <div className="text-[11px] font-mono text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-sm px-3 py-2 flex items-center justify-between">
              <span>
                {status.assets.filter((a) => !a.has_source).length} scheduled asset{status.assets.filter((a) => !a.has_source).length === 1 ? "" : "s"} have no MP4 source file.
              </span>
              <a
                href="/console/assets"
                className="text-fuchsia-400 hover:text-fuchsia-300 font-display font-bold text-[11px]"
                data-testid={`publish-today-upload-link-${channel.slug}`}
              >
                Upload files →
              </a>
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button variant="outline" size="sm" className="rounded-sm h-8 text-xs" onClick={() => setOpen(false)}>Close</Button>
            <Button
              size="sm"
              onClick={publish}
              disabled={busy || !canWrite || anyInFlight || status.total === status.ready || publishableCount === 0}
              className="rounded-sm h-8 text-xs tenant-btn"
              data-testid={`publish-today-run-${channel.slug}`}
              title={
                !canWrite ? "Read-only role"
                : status.total === status.ready ? "Everything already on CDN"
                : anyInFlight ? "Publish in progress"
                : publishableCount === 0 ? "No unpublished asset has a source file"
                : "Queue every unpublished asset scheduled today"
              }
            >
              {busy || anyInFlight ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CloudUpload className="w-3.5 h-3.5 mr-1" />}
              {status.total === status.ready
                ? "All ready"
                : publishableCount === 0
                  ? "No source files"
                  : `Publish ${publishableCount} to CDN`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
